import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type { DiagnosticRun, DiagnosticRunList } from "@/api/types";

const KEYS = {
  forSession: (sid: string) => ["diagnostic", "session", sid] as const,
  detail: (id: string) => ["diagnostic", "run", id] as const,
};

export function useSessionDiagnosticRuns(sessionId?: string) {
  return useQuery({
    queryKey: sessionId ? KEYS.forSession(sessionId) : ["diagnostic", "noop"],
    queryFn: () =>
      api<DiagnosticRunList>(
        `/api/v1/analysis/sessions/${sessionId}/diagnostic/runs`,
      ),
    enabled: Boolean(sessionId),
    // Polling rápido para que las propuestas pending aparezcan apenas
    // el modelo las crea al cerrar un turno; la sesión está siendo
    // observada activamente por el operador.
    refetchInterval: 5_000,
  });
}

export function useApproveDiagnostic(sessionId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      api<DiagnosticRun>(
        `/api/v1/analysis/diagnostic/${runId}/approve`,
        { method: "POST" },
      ),
    onSuccess: () => {
      // Invalida lista de runs + detalle de sesión (mensajes nuevos
      // del modelo tras el output).
      if (sessionId) {
        qc.invalidateQueries({ queryKey: KEYS.forSession(sessionId) });
        qc.invalidateQueries({ queryKey: ["analysis", "session", sessionId] });
      }
    },
  });
}

export function useRejectDiagnostic(sessionId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, reason }: { runId: string; reason?: string }) =>
      api<DiagnosticRun>(`/api/v1/analysis/diagnostic/${runId}/reject`, {
        method: "POST",
        body: reason ? { reason } : {},
      }),
    onSuccess: () => {
      if (sessionId) {
        qc.invalidateQueries({ queryKey: KEYS.forSession(sessionId) });
        qc.invalidateQueries({ queryKey: ["analysis", "session", sessionId] });
      }
    },
  });
}
